"use client"

/**
 * Paper roster inside a section card. Embedded DataTable (not HubTable) with a
 * per-section filter bar so authors can narrow rows without leaving the paper.
 */

import * as React from "react"
import { createPortal } from "react-dom"
import { useSortable } from "@dnd-kit/sortable"

import { DataTable } from "@/components/data-table"
import type { ColumnDef } from "@/components/data-table/types"
import { useTableState } from "@/components/data-table/use-table-state"
import {
  columnsToFieldDefinitions,
  columnsToFilterFields,
  EmptyCell,
  NumericCell,
  PillCell,
  TableNewRowDot,
  TruncatedControl,
  TruncatedText,
} from "@/components/data-views"
import { TablePropertiesDrawerButton } from "@/components/table-properties"
import {
  DEFAULT_DATA_LIST_DISPLAY_OPTIONS,
  type DataListDisplayOptions,
} from "@exxatdesignux/ui/lib/data-list-display-options"
import { LeoSourceMark } from "@/components/assessment-structure/leo-source-mark"
import { QuestionLeoAlterMenu } from "@/components/assessment-structure/question-leo-alter-menu"
import {
  NeedsReviewMark,
  NEEDS_REVIEW_ROW_BG,
} from "@/components/assessment-structure/needs-review-mark"
import { QuestionBankStatusBadge } from "@/components/assessment-structure/question-bank-status-badge"
import {
  DIFFICULTIES,
  difficultyBars,
  difficultyBarsLabelled,
  questionTypePill,
} from "@/components/assessment-structure/question-column-chips"
import { chipFilterOptions } from "@/lib/column-filter-rich-options"
import { QUESTION_BANK_STATUSES } from "@/lib/question-bank-status"
import { useQuestionPreview } from "@/components/assessment-structure/question-preview-context"
import { railTriggerProps } from "@/lib/floating-sheet-panel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { StatusBadge } from "@/components/ui/status-badge"
import { questionSignalInputFromPaperRow, paperRowUpperLower } from "@/lib/question-preview-adapters"
import { percent } from "@/components/assessment-structure/band-scale-shared"
import { expectedMinutesForQuestionType } from "@/lib/question-expected-time"
import {
  paperQuestionNeedsReview,
  PBI_REVIEW_FLOOR_DEFAULT,
  type PbiReviewThreshold,
} from "@/lib/assessment-question-review"
import { pbiReviewFloorFromThreshold } from "@/lib/pbi-review-floor"
import { getConditionalRowBackground } from "@/lib/conditional-rule-match"
import {
  questionReviewSignals,
  formatReviewReason,
} from "@/lib/question-signals"
import { BLUEPRINT_ICON } from "@/lib/blueprint/icons"
import { pbiBand } from "@/lib/pbi-bands"
import { dIndexBand } from "@/lib/discrimination"
import type { QuestionRecord } from "@/src/pages/assessment-details"
import { type ConditionalRule } from "@/components/table-properties/types"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@exxatdesignux/ui/components/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@exxatdesignux/ui/components/tooltip"
import { Tip } from "@/components/ui/tip"
import { cn } from "@/lib/utils"
import { BlueprintAlignCell } from "@/components/assessment-structure/blueprint-align-cell"
import { LeoBrandBackdrop } from "@exxatdesignux/ui/components/leo-brand-backdrop"
import { LeoGenerationStatus } from "@exxatdesignux/ui/components/leo-generation-surface"
import { LEO_SPOTTED_COLUMN } from "@/components/question-quality/leo-spotted-cell-shared"
import { LeoSpottedColumnHeader } from "@/components/question-quality/leo-spotted-column-header"
import { LeoSpottedTableCell } from "@/components/question-quality/leo-spotted-cell"
import { blueprintAlignmentForSubject } from "@/lib/blueprint/alignment"
import { leoSpottedSubjectFromBankRow } from "@/lib/question-quality/leo-spotted-source"
import { questionCatalogById } from "@/lib/mock/question-catalog"
import {
  isLeoBusyQuestion,
  paperQuestionBlueprintExplain,
  questionMissesSectionPlan,
} from "@/lib/leo-blueprint-section-fill"

type SectionQuestionRow = QuestionRecord & {
  needsReview: string
  blueprintAlert: string
} & Record<string, unknown>

const NEEDS_REVIEW_VALUE = "Needs review"
const BLUEPRINT_NOT_ALIGNED = "Not aligned"
const BLUEPRINT_ALIGNED = "Aligned"
const NEEDS_REVIEW_BG = NEEDS_REVIEW_ROW_BG

const Q_NUMBER_KEY = "number"

function paperRosterConditionalRules(): ConditionalRule[] {
  return [
    {
      id: "needs-review-row",
      fieldKey: "needsReview",
      operator: "is",
      values: [NEEDS_REVIEW_VALUE],
      bgColor: NEEDS_REVIEW_BG,
    },
  ]
}

function withNeedsReviewSortKey(
  col: ColumnDef<SectionQuestionRow>,
): ColumnDef<SectionQuestionRow> {
  return {
    ...col,
    sortable: false,
    ...(col.key === Q_NUMBER_KEY ? { sortKey: "needsReview" as const } : {}),
  }
}

const TYPE_SHORT: Record<string, string> = {
  "Multiple Choice": "MCQ",
  Hotspot: "Hotspot",
  "Multiple Select": "MSQ",
  Essay: "Essay",
  "True or False": "T/F",
  "Fill in the blank": "FIB",
  "Match the following": "Match",
}

const TYPE_ICON: Record<string, string> = {
  "Multiple Choice": "fa-list-check",
  "Multiple Select": "fa-square-check",
  Hotspot: "fa-bullseye",
  Essay: "fa-pen-line",
  "True or False": "fa-toggle-on",
  "Fill in the blank": "fa-i-cursor",
  "Match the following": "fa-arrows-left-right",
}

function ReorderHandle({
  question,
  disabled,
}: {
  question: QuestionRecord
  disabled?: boolean
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    isDragging,
  } = useSortable({
    id: question.id,
    disabled,
    animateLayoutChanges: () => false,
    transition: null,
    resizeObserverConfig: { disabled: true },
  })
  const rowRef = React.useRef<HTMLTableRowElement | null>(null)

  const setHandleRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      setActivatorNodeRef(node)
      const row = node?.closest("tr") as HTMLTableRowElement | null
      rowRef.current = row
      if (row) setNodeRef(row)
    },
    [setActivatorNodeRef, setNodeRef],
  )

  React.useLayoutEffect(() => {
    const row = rowRef.current
    if (!row) return
    row.style.opacity = isDragging ? "0.45" : ""
    return () => {
      row.style.opacity = ""
    }
  }, [isDragging])

  const label = `Reorder question ${question.number}`

  return (
    <Tip label={label} triggerClassName="!cursor-grab">
      <button
        ref={setHandleRef}
        type="button"
        {...attributes}
        {...listeners}
        data-row-control
        disabled={disabled}
        aria-label={label}
        className="inline-flex size-6 min-h-6 min-w-6 shrink-0 items-center justify-center rounded-1 text-muted-foreground/50 hover:text-muted-foreground !cursor-grab active:!cursor-grabbing focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:!cursor-default disabled:opacity-40"
      >
        <i className="fa-solid fa-grip-dots-vertical pointer-events-none text-sm" aria-hidden />
      </button>
    </Tip>
  )
}

function applyPaperRowCellBackground(
  row: HTMLTableRowElement,
  background: string | null,
) {
  for (const cell of Array.from(row.cells)) {
    if (background) {
      cell.style.setProperty("background-color", background, "important")
    } else {
      cell.style.removeProperty("background-color")
    }
  }
}

type PaperRosterContextValue = {
  conditionalRules: ConditionalRule[]
  columns: ColumnDef<SectionQuestionRow>[]
  pbiReviewThreshold: PbiReviewThreshold
}

const PaperRosterContext = React.createContext<PaperRosterContextValue | null>(
  null,
)

function usePaperRosterContext() {
  const value = React.useContext(PaperRosterContext)
  if (!value) {
    throw new Error("PaperRosterContext is missing")
  }
  return value
}

/** Syncs needs-review, thinking blob wash, and conditional-rule row washes onto every `<td>`. */
function PaperRosterRowAttrs({ row }: { row: SectionQuestionRow }) {
  const ref = React.useRef<HTMLSpanElement>(null)
  const [host, setHost] = React.useState<HTMLTableRowElement | null>(null)
  const { conditionalRules, columns } = usePaperRosterContext()
  const openPreview = useQuestionPreview()
  const thinking = isLeoBusyQuestion(row)
  const needsReview = !thinking && row.needsReview === NEEDS_REVIEW_VALUE
  const conditionalRowBg = React.useMemo(
    () =>
      thinking
        ? null
        : getConditionalRowBackground(row, conditionalRules, columns),
    [row, conditionalRules, columns, thinking],
  )
  const rowBackground =
    conditionalRowBg ?? (needsReview ? NEEDS_REVIEW_BG : null)

  React.useLayoutEffect(() => {
    const tableRow = ref.current?.closest("tr") as HTMLTableRowElement | null
    setHost(tableRow)
    if (!tableRow) return

    if (openPreview) {
      tableRow.setAttribute("data-rail-trigger", "")
    } else {
      tableRow.removeAttribute("data-rail-trigger")
    }

    if (needsReview) {
      tableRow.dataset.needsReview = "true"
    } else {
      delete tableRow.dataset.needsReview
    }

    if (thinking) {
      tableRow.dataset.leoThinking = "true"
      tableRow.classList.add("relative", "overflow-hidden")
      tableRow.style.scrollMarginTop =
        "calc(var(--shell-utility-bar-height) + var(--assessment-section-header-height, 3.25rem) + 0.75rem)"
      for (const cell of Array.from(tableRow.cells)) {
        cell.classList.add("relative", "z-[1]")
        cell.style.setProperty("background-color", "transparent", "important")
      }
      tableRow.scrollIntoView({ block: "center", behavior: "smooth" })
    } else if (rowBackground) {
      tableRow.dataset.conditionalRowBg = "true"
      tableRow.style.setProperty("--conditional-row-bg", rowBackground)
      applyPaperRowCellBackground(tableRow, rowBackground)
    } else {
      delete tableRow.dataset.conditionalRowBg
      tableRow.style.removeProperty("--conditional-row-bg")
      applyPaperRowCellBackground(tableRow, null)
    }

    return () => {
      delete tableRow.dataset.needsReview
      delete tableRow.dataset.conditionalRowBg
      delete tableRow.dataset.leoThinking
      tableRow.removeAttribute("data-rail-trigger")
      tableRow.classList.remove("relative", "overflow-hidden")
      tableRow.style.removeProperty("scroll-margin-top")
      tableRow.style.removeProperty("--conditional-row-bg")
      applyPaperRowCellBackground(tableRow, null)
      for (const cell of Array.from(tableRow.cells)) {
        cell.classList.remove("relative", "z-[1]")
      }
    }
  }, [
    needsReview,
    openPreview,
    rowBackground,
    thinking,
    row.id,
    row.leoAct,
  ])

  return (
    <>
      <span ref={ref} hidden aria-hidden />
      {thinking && host
        ? createPortal(
            // The package's Leo generation wash: one brand colour across the row.
            <LeoBrandBackdrop className="rounded-none" />,
            host,
          )
        : null}
    </>
  )
}

function paperRowReviewReasons(
  row: SectionQuestionRow,
  pbiReviewThreshold: PbiReviewThreshold,
) {
  return questionReviewSignals({
    ...questionSignalInputFromPaperRow(row),
    pbiReviewFloor: pbiReviewFloorFromThreshold(pbiReviewThreshold),
  }).map(formatReviewReason)
}

function linkedGroupMeta(
  questions: QuestionRecord[],
  questionId: string,
): { groupId: string; size: number } | null {
  const question = questions.find((row) => row.id === questionId)
  const groupId = question?.groupId
  if (!groupId) return null
  const members = questions.filter((row) => row.groupId === groupId)
  if (members.length < 2) return null
  return { groupId, size: members.length }
}

function LinkedQuestionsMark({
  groupId,
  size,
  onUngroup,
  isReadOnly,
}: {
  groupId: string
  size: number
  onUngroup?: (groupId: string) => void
  isReadOnly?: boolean
}) {
  const canUngroup = Boolean(onUngroup) && !isReadOnly
  const label = canUngroup
    ? `Ungroup ${size} linked questions`
    : `${size} questions linked`

  return (
    <Tip label={label}>
      <button
        type="button"
        data-row-control
        disabled={!canUngroup}
        aria-label={label}
        onClick={() => onUngroup?.(groupId)}
        className="inline-flex size-6 min-h-6 min-w-6 shrink-0 items-center justify-center rounded-1 text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-70"
      >
        <i className="fa-solid fa-link text-xs" aria-hidden />
      </button>
    </Tip>
  )
}

function MarksBadge({
  points,
  locked,
}: {
  points: number
  locked?: boolean
}) {
  const label = `${points} ${points === 1 ? "mark" : "marks"}`
  return (
    <Badge
      variant="secondary"
      className="min-w-6 justify-center gap-1 px-1.5 tabular-nums"
      aria-label={label}
    >
      {locked ? (
        <i
          className="fa-light fa-lock text-xs text-muted-foreground"
          aria-hidden
        />
      ) : null}
      <span aria-hidden>{points}</span>
    </Badge>
  )
}

function LeoThinkingStemCell({ question }: { question: QuestionRecord }) {
  const surfaceRef = React.useRef<HTMLDivElement>(null)

  React.useLayoutEffect(() => {
    surfaceRef.current?.scrollIntoView({ block: "center", behavior: "smooth" })
  }, [question.id, question.leoAct])

  return (
    <div
      ref={surfaceRef}
      data-slot="leo-thinking-stem"
      className="relative z-[1] min-w-0 scroll-mt-[calc(var(--shell-utility-bar-height)+var(--assessment-section-header-height,3.25rem)+0.75rem)]"
    >
      <LeoGenerationStatus state="thinking" label={question.leoAct ?? "Leo is drafting this item"} />
      {question.leoActDetail ? (
        <p className="m-0 mt-0.5 ps-7 text-sm leading-5 text-muted-foreground">{question.leoActDetail}</p>
      ) : null}
    </div>
  )
}

function StemCell({ question }: { question: QuestionRecord }) {
  const openPreview = useQuestionPreview()
  if (isLeoBusyQuestion(question)) {
    return <LeoThinkingStemCell question={question} />
  }

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col",
        question.status === "Draft" && "opacity-75",
      )}
    >
      <div className="flex min-w-0 items-start gap-2">
        {question.isNew ? <TableNewRowDot className="mt-1.5" /> : null}
        {openPreview ? (
          <TruncatedControl label={question.stem}>
            {(ref) => (
              <button
                ref={ref as React.RefCallback<HTMLButtonElement>}
                type="button"
                onClick={() => openPreview(question)}
                {...railTriggerProps()}
                className={cn(
                  "min-w-0 line-clamp-2 rounded-1 text-left text-sm font-normal leading-normal hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                  question.status === "Draft"
                    ? "text-muted-foreground"
                    : "text-foreground/90",
                )}
              >
                {question.stem}
              </button>
            )}
          </TruncatedControl>
        ) : (
          <TruncatedText
            text={question.stem}
            className={cn(
              "line-clamp-2 whitespace-normal text-left text-sm font-normal leading-normal",
              question.status === "Draft"
                ? "text-muted-foreground"
                : "text-foreground/90",
            )}
          />
        )}
        {question.status && question.status !== "Published" ? (
          <QuestionBankStatusBadge status={question.status} size="md" />
        ) : null}
      </div>
      {question.title ? (
        <TruncatedText
          text={question.title}
          className="truncate text-xs text-muted-foreground"
        />
      ) : null}
      {question.leoSource ? (
        <LeoSourceMark source={question.leoSource} className="mt-0.5" />
      ) : null}
    </div>
  )
}

function MetricWithCaptionCell({
  value,
  caption,
  fractionDigits = 2,
  weak = false,
  empty = false,
}: {
  value: number
  caption: string
  fractionDigits?: number
  weak?: boolean
  empty?: boolean
}) {
  if (empty) return <EmptyCell />
  return (
    <span className="flex min-w-0 flex-col items-end gap-0.5 text-end">
      <NumericCell
        value={value}
        fractionDigits={fractionDigits}
        className={cn(
          "text-xs font-semibold",
          weak ? "text-destructive font-bold" : "text-muted-foreground",
        )}
      />
      <span className="max-w-full text-pretty text-[11px] leading-tight text-muted-foreground">
        {caption}
      </span>
    </span>
  )
}

function buildMarksColumn({
  isReadOnly,
  onUpdatePoints,
  onToggleLock,
}: {
  isReadOnly: boolean
  onUpdatePoints?: (qId: string, val: number) => void
  onToggleLock?: (qId: string) => void
}): ColumnDef<SectionQuestionRow> {
  return {
    key: "points",
    label: "Marks",
    width: 56,
    minWidth: 48,
    cellKind: "numeric",
      cell: (row) =>
        isLeoBusyQuestion(row) ? null : isReadOnly ? (
        <MarksBadge points={row.points} locked={row.pointsLocked} />
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto p-0 font-normal hover:bg-transparent"
              aria-label={`${row.points} ${row.points === 1 ? "mark" : "marks"}`}
            >
              <MarksBadge points={row.points} locked={row.pointsLocked} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-3" align="end" side="top">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Edit marks</span>
                {row.pointsLocked ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => onToggleLock?.(row.id)}
                  >
                    <i className="fa-light fa-unlock mr-1.5" aria-hidden />{" "}
                    Unlock
                  </Button>
                ) : null}
              </div>
              <Input
                type="number"
                min="0"
                step="any"
                className="tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                value={row.points}
                onChange={(e) => {
                  const val = e.target.value === "" ? 0 : Number(e.target.value)
                  onUpdatePoints?.(row.id, val)
                }}
                onFocus={(e) => e.target.select()}
                autoFocus
              />
              <p className="text-xs leading-normal text-muted-foreground">
                Setting a custom score locks it and excludes it from
                auto-distribution.
              </p>
            </div>
          </PopoverContent>
        </Popover>
      ),
  }
}

function buildEstTimeColumn(): ColumnDef<SectionQuestionRow> {
  return {
    key: "estTime",
    label: "Est. time",
    width: 72,
    minWidth: 56,
    cell: (row) =>
      isLeoBusyQuestion(row) ? null : (
        <span className="block text-center font-mono text-xs tabular-nums text-muted-foreground">
          {expectedMinutesForQuestionType(row.type)} min
        </span>
      ),
  }
}

function buildBlueprintColumn(
  sectionId: string,
): ColumnDef<SectionQuestionRow> {
  return {
    key: "blueprintAlert",
    label: "Blueprint",
    width: 168,
    minWidth: 148,
    defaultPin: "right",
    lockPin: true,
    cellKind: "status",
    filter: {
      type: "select",
      icon: BLUEPRINT_ICON,
      options: [
        { value: BLUEPRINT_ALIGNED, label: BLUEPRINT_ALIGNED },
        { value: BLUEPRINT_NOT_ALIGNED, label: BLUEPRINT_NOT_ALIGNED },
      ],
    },
    cell: (row) => {
      if (isLeoBusyQuestion(row) || !row.blueprintAlert) return null
      return (
        <BlueprintAlignCell
          explain={paperQuestionBlueprintExplain(sectionId, row)}
        />
      )
    },
  }
}

function buildColumns({
  focusMode,
  isReadOnly,
  onUpdatePoints,
  onToggleLock,
  onEdit,
  onRemove,
  onMove,
  onUngroup,
  onRewrite,
  onReplace,
  onPickFromBank,
  onOpenPreview,
  questions,
  pbiReviewThreshold,
  sectionId,
}: {
  focusMode: "content" | "analytics"
  isReadOnly: boolean
  onUpdatePoints?: (qId: string, val: number) => void
  onToggleLock?: (qId: string) => void
  onEdit?: (question: QuestionRecord) => void
  onRemove?: (questionIds: string[]) => void
  onMove?: (questionId: string, direction: -1 | 1) => void
  onUngroup?: (groupId: string) => void
  onRewrite?: (question: QuestionRecord) => void
  onReplace?: (question: QuestionRecord) => void
  onPickFromBank?: (question: QuestionRecord) => void
  onOpenPreview?: (question: QuestionRecord) => void
  questions: QuestionRecord[]
  pbiReviewThreshold: PbiReviewThreshold
  sectionId: string
}): ColumnDef<SectionQuestionRow>[] {
  const cols: ColumnDef<SectionQuestionRow>[] = []

  if (!isReadOnly) {
    cols.push(
      {
        key: "reorder",
        label: "",
        width: 40,
        minWidth: 40,
        resizable: false,
        defaultPin: "left",
        lockPin: true,
        cell: (row) => (
          <div className="flex items-center justify-center">
            <ReorderHandle
              question={row}
              disabled={Boolean(row.groupId) || isLeoBusyQuestion(row)}
            />
          </div>
        ),
      },
    )
  }

  cols.push({
    key: Q_NUMBER_KEY,
    label: "Q#",
    width: 96,
    minWidth: 80,
    resizable: false,
    defaultPin: "left",
    lockPin: true,
    cellKind: "text",
    cell: (row) => {
      const reviewReasons = paperRowReviewReasons(row, pbiReviewThreshold)
      const linked = linkedGroupMeta(questions, row.id)
      return (
        <div className="flex min-w-0 items-center gap-1 overflow-hidden">
          <PaperRosterRowAttrs row={row} />
          {linked ? (
            <LinkedQuestionsMark
              groupId={linked.groupId}
              size={linked.size}
              onUngroup={onUngroup}
              isReadOnly={isReadOnly}
            />
          ) : null}
          <span className="shrink-0 text-start font-mono text-xs tabular-nums text-muted-foreground">
            Q{row.number}
          </span>
          {reviewReasons.length > 0 ? (
            <NeedsReviewMark reasons={reviewReasons} />
          ) : null}
        </div>
      )
    },
  })

  const typeOptions = [...new Set(questions.map((row) => row.type))].sort()

  cols.push({
    key: "stem",
    label: "Question Stem",
    width: 280,
    minWidth: 160,
    defaultPin: "left",
    lockPin: true,
    cellKind: "text",
    filter: {
      type: "text",
      icon: "fa-font",
      operators: ["contains", "not_contains"],
    },
    cell: (row) => <StemCell question={row} />,
  })

  const typeFilter = {
    type: "select" as const,
    icon: "fa-list",
    options: chipFilterOptions(typeOptions, questionTypePill),
  }

  if (focusMode === "content") {
    cols.push({
      key: "type",
      label: "Type",
      width: 80,
      minWidth: 64,
      cellKind: "pill",
      filter: typeFilter,
      cell: (row) =>
        isLeoBusyQuestion(row) ? null : (
        <PillCell
          label={TYPE_SHORT[row.type] || row.type}
          icon={TYPE_ICON[row.type] ?? "fa-list-check"}
        />
        ),
    })
  }

  cols.push({
    ...LEO_SPOTTED_COLUMN,
    header: () => <LeoSpottedColumnHeader />,
    cell: (row) => (
      <LeoSpottedTableCell
        subject={leoSpottedSubjectFromBankRow(row, {
          alignment: blueprintAlignmentForSubject(sectionId, row),
        })}
        busy={isLeoBusyQuestion(row)}
        onOpenSheet={onOpenPreview ? () => onOpenPreview(row) : undefined}
      />
    ),
  })

  const difficultyFilter = {
    type: "select" as const,
    icon: "fa-gauge",
    options: chipFilterOptions([...DIFFICULTIES], difficultyBarsLabelled),
  }

  if (focusMode === "analytics") {
    cols.push(
      {
        key: "upper27",
        label: "Upper 27%",
        width: 88,
        minWidth: 72,
        cellKind: "numeric",
        cell: (row) => {
          if (isLeoBusyQuestion(row)) return null
          if (!row.usedBefore) return <EmptyCell />
          const { upper27 } = paperRowUpperLower(row)
          if (upper27 === null) return <EmptyCell />
          return (
            <span className="block text-center text-xs font-semibold tabular-nums text-muted-foreground">
              {percent(upper27)}
            </span>
          )
        },
      },
      {
        key: "lower27",
        label: "Lower 27%",
        width: 88,
        minWidth: 72,
        cellKind: "numeric",
        cell: (row) => {
          if (isLeoBusyQuestion(row)) return null
          if (!row.usedBefore) return <EmptyCell />
          const { lower27 } = paperRowUpperLower(row)
          if (lower27 === null) return <EmptyCell />
          return (
            <span className="block text-center text-xs font-semibold tabular-nums text-muted-foreground">
              {percent(lower27)}
            </span>
          )
        },
      },
      {
        key: "discriminationIndex",
        label: "Disc. Index",
        width: 120,
        minWidth: 96,
        cellKind: "numeric",
        cell: (row) => {
          if (isLeoBusyQuestion(row)) return null
          if (!row.usedBefore) return <EmptyCell />
          const value = row.discriminationIndex || 0
          const band = dIndexBand(value)
          return (
            <MetricWithCaptionCell
              value={value}
              caption={band.label}
              weak={band.key === "weak"}
            />
          )
        },
      },
      {
        key: "pointBiserial",
        label: "PBI",
        width: 104,
        minWidth: 88,
        cellKind: "numeric",
        cell: (row) => {
          if (isLeoBusyQuestion(row)) return null
          if (!row.usedBefore) return <EmptyCell />
          const band = pbiBand(row.pointBiserial)
          return (
            <MetricWithCaptionCell
              value={row.pointBiserial}
              caption={band.label}
              weak={band.key === "weak"}
            />
          )
        },
      },
      {
        key: "difficultyPercentage",
        label: "Difficulty %",
        width: 112,
        minWidth: 88,
        cellKind: "numeric",
        cell: (row) =>
          isLeoBusyQuestion(row) ? null : !row.usedBefore ? (
            <EmptyCell />
          ) : (
            <span className="block text-center text-xs font-semibold tabular-nums text-muted-foreground">
              {row.difficultyPercentage}%
            </span>
          ),
      },
      {
        key: "usedCount",
        label: "Usage",
        width: 100,
        minWidth: 72,
        cell: (row) =>
          isLeoBusyQuestion(row) ? null : row.usedBefore ? (
            <span
              className={cn(
                "flex min-w-0 items-center gap-1 text-xs",
                row.usedInSameCourse
                  ? "font-bold text-destructive"
                  : "text-muted-foreground",
              )}
            >
              <i className="fa-light fa-clock-rotate-left text-xs" aria-hidden />
              <span>{row.usedCount}x</span>
            </span>
          ) : (
            <StatusBadge label="New" tone="success" size="sm" />
          ),
      },
    )
    cols.push(
      buildEstTimeColumn(),
      buildMarksColumn({ isReadOnly, onUpdatePoints, onToggleLock }),
      buildBlueprintColumn(sectionId),
    )
  } else {
    cols.push(
      buildEstTimeColumn(),
      {
        key: "difficulty",
        label: "Difficulty",
        width: 88,
        minWidth: 64,
        cellKind: "signal-bars",
        filter: difficultyFilter,
        cell: (row) =>
          isLeoBusyQuestion(row) ? null : row.difficulty === "Easy" ||
            row.difficulty === "Medium" ||
            row.difficulty === "Hard" ? (
            difficultyBars(row.difficulty)
          ) : (
            <EmptyCell label="No difficulty" />
          ),
      },
      buildMarksColumn({ isReadOnly, onUpdatePoints, onToggleLock }),
      buildBlueprintColumn(sectionId),
    )
  }

  if (!isReadOnly) {
    cols.push({
      key: "rowActions",
      label: "Actions",
      width: 170,
      minWidth: 120,
      resizable: false,
      defaultPin: "right",
      lockPin: true,
      cell: (row) => {
        const index = questions.findIndex((q) => q.id === row.id)
        const canMoveUp = index > 0 && !row.groupId
        const canMoveDown =
          index >= 0 && index < questions.length - 1 && !row.groupId
        return (
          <div
            className="flex items-center justify-start gap-0.5"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {isLeoBusyQuestion(row) ? (
              <span className="sr-only">Leo is still writing this item</span>
            ) : (
              <>
            {onMove && !row.groupId ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      disabled={!canMoveUp}
                      onClick={() => onMove(row.id, -1)}
                      aria-label={`Move question ${row.number} up`}
                    >
                      <i className="fa-light fa-arrow-up" aria-hidden />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Move up</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      disabled={!canMoveDown}
                      onClick={() => onMove(row.id, 1)}
                      aria-label={`Move question ${row.number} down`}
                    >
                      <i className="fa-light fa-arrow-down" aria-hidden />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Move down</TooltipContent>
                </Tooltip>
              </>
            ) : null}
            <QuestionLeoAlterMenu
              questionNumber={row.number}
              onRewrite={onRewrite ? () => onRewrite(row) : undefined}
              onReplace={onReplace ? () => onReplace(row) : undefined}
              onPickFromBank={
                onPickFromBank ? () => onPickFromBank(row) : undefined
              }
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => onEdit?.(row)}
                  aria-label="Edit question"
                >
                  <i className="fa-light fa-pen" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => onRemove?.([row.id])}
                  aria-label="Remove question"
                >
                  <i className="fa-light fa-xmark" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Remove from paper</TooltipContent>
            </Tooltip>
              </>
            )}
          </div>
        )
      },
    })
  }

  if (focusMode === "analytics") {
    cols.push(
      {
        key: "type",
        label: "Type",
        filterOnly: true,
        filter: typeFilter,
      },
      {
        key: "difficulty",
        label: "Difficulty",
        filterOnly: true,
        filter: difficultyFilter,
      },
    )
  }

  cols.push(
    {
      key: "status",
      label: "Status",
      filterOnly: true,
      filter: {
        type: "select",
        icon: "fa-circle-dot",
        options: chipFilterOptions(
          [...QUESTION_BANK_STATUSES],
          (value) => <QuestionBankStatusBadge status={value} showReady />,
        ),
      },
    },
    {
      key: "needsReview",
      label: "Needs review",
      filterOnly: true,
      filter: {
        type: "select",
        icon: "fa-triangle-exclamation",
        options: [{ value: NEEDS_REVIEW_VALUE, label: NEEDS_REVIEW_VALUE }],
      },
    },
  )

  return cols.map(withNeedsReviewSortKey)
}

export function SectionQuestionTable({
  sectionId,
  questions,
  focusMode,
  isReadOnly = false,
  onRemoveQuestions,
  onUpdatePoints,
  onToggleLock,
  onEditQuestion,
  onMoveQuestion,
  onUngroup,
  pbiReviewThreshold = PBI_REVIEW_FLOOR_DEFAULT,
  onRewriteQuestion,
  onReplaceQuestion,
  onPickQuestionFromBank,
}: {
  sectionId: string
  questions: QuestionRecord[]
  focusMode: "content" | "analytics"
  pbiReviewThreshold?: PbiReviewThreshold
  isReadOnly?: boolean
  onRemoveQuestions?: (questionIds: string[]) => void
  onUpdatePoints?: (qId: string, val: number) => void
  onToggleLock?: (qId: string) => void
  onEditQuestion?: (question: QuestionRecord) => void
  onMoveQuestion?: (questionId: string, direction: -1 | 1) => void
  onUngroup?: (groupId: string) => void
  onRewriteQuestion?: (question: QuestionRecord) => void
  onReplaceQuestion?: (question: QuestionRecord) => void
  onPickQuestionFromBank?: (question: QuestionRecord) => void
}) {
  const openPreview = useQuestionPreview()
  const [conditionalRules, setConditionalRules] = React.useState<
    ConditionalRule[]
  >(() => paperRosterConditionalRules())

  const columns = React.useMemo(
    () =>
      buildColumns({
        focusMode,
        isReadOnly,
        onUpdatePoints,
        onToggleLock,
        onEdit: onEditQuestion,
        onRemove: onRemoveQuestions,
        onMove: onMoveQuestion,
        onUngroup,
        onRewrite: onRewriteQuestion,
        onReplace: onReplaceQuestion,
        onPickFromBank: onPickQuestionFromBank,
        onOpenPreview: openPreview ?? undefined,
        questions,
        pbiReviewThreshold,
        sectionId,
      }),
    [
      focusMode,
      isReadOnly,
      onEditQuestion,
      onMoveQuestion,
      onRemoveQuestions,
      onRewriteQuestion,
      onReplaceQuestion,
      onPickQuestionFromBank,
      onToggleLock,
      onUngroup,
      onUpdatePoints,
      openPreview,
      questions,
      pbiReviewThreshold,
      sectionId,
    ],
  )
  const rosterContext = React.useMemo(
    () => ({ conditionalRules, columns, pbiReviewThreshold }),
    [conditionalRules, columns, pbiReviewThreshold],
  )

  const rows = React.useMemo(
    () =>
      questions.map((question) => ({
        ...question,
        status: question.status ?? "Published",
        needsReview:
          !isLeoBusyQuestion(question) &&
          paperQuestionNeedsReview(question, pbiReviewThreshold)
            ? NEEDS_REVIEW_VALUE
            : "",
        blueprintAlert: isLeoBusyQuestion(question)
          ? ""
          : questionMissesSectionPlan(sectionId, question)
            ? BLUEPRINT_NOT_ALIGNED
            : BLUEPRINT_ALIGNED,
      })),
    [questions, pbiReviewThreshold, sectionId],
  )
  const state = useTableState(rows, columns)
  const filterFields = React.useMemo(
    () => columnsToFilterFields(columns),
    [columns],
  )
  const fieldDefinitions = React.useMemo(
    () => columnsToFieldDefinitions(columns),
    [columns],
  )
  const resolveColumnLabel = React.useCallback(
    (key: string) => columns.find((col) => col.key === key)?.label ?? key,
    [columns],
  )
  const [displayOptions, setDisplayOptions] =
    React.useState<DataListDisplayOptions>(DEFAULT_DATA_LIST_DISPLAY_OPTIONS)
  const patchDisplayOptions = React.useCallback(
    (patch: Partial<DataListDisplayOptions>) =>
      setDisplayOptions((prev) => ({ ...prev, ...patch })),
    [],
  )
  const addConditionalRule = React.useCallback(
    (rule: Omit<ConditionalRule, "id">) =>
      setConditionalRules((prev) => [
        ...prev,
        { ...rule, id: `cr-${Date.now()}` },
      ]),
    [],
  )
  const removeConditionalRule = React.useCallback(
    (id: string) =>
      setConditionalRules((prev) => prev.filter((rule) => rule.id !== id)),
    [],
  )
  const updateConditionalRule = React.useCallback(
    (id: string, patch: Partial<ConditionalRule>) =>
      setConditionalRules((prev) =>
        prev.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)),
      ),
    [],
  )

  return (
    <PaperRosterContext.Provider value={rosterContext}>
      <div data-paper-roster="" className="min-w-0">
        <DataTable
        data={rows}
        columns={columns}
        state={state}
        getRowId={(row) => row.id}
        selectable={false}
        searchable={displayOptions.showToolbarSearch}
        showQueryControls={!isReadOnly}
        showColumnHeaders={displayOptions.showColumnLabels}
        edgeInset={false}
        addRowLabel={false}
        conditionalRules={conditionalRules}
        emptyState="This section is empty."
        onRowClick={openPreview ? (row) => openPreview(row) : undefined}
        toolbarSlot={(tableState) => (
          <TablePropertiesDrawerButton
            state={tableState}
            totalRows={rows.length}
            filterFields={filterFields}
            fieldDefinitions={fieldDefinitions}
            resolveColumnLabel={resolveColumnLabel}
            displayOptions={displayOptions}
            onDisplayOptionsChange={patchDisplayOptions}
            conditionalRules={conditionalRules}
            onAddConditionalRule={addConditionalRule}
            onRemoveConditionalRule={removeConditionalRule}
            onUpdateConditionalRule={updateConditionalRule}
            supportedViewTypes={["table"]}
            currentView="table"
            lifecycleTabLabel="Section questions"
          />
        )}
      />
      </div>
    </PaperRosterContext.Provider>
  )
}
